import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import {
  Badge, Button, Card, Dialog, EmptyState, ErrorState, Field, Input, Loading, Select,
} from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import { minorFromRupees, money, number, rupeesFromMinor } from '../../lib/format.js';

/**
 * The two catalogues.
 *
 * "Owners and tenants" rather than "owners": the same list is bought from by
 * somebody letting their flat and by somebody paying to see who to call, and
 * calling it one of them would be wrong about the other.
 */
const AUDIENCES = [
  { value: 'customer', label: 'Owners and tenants', short: 'Owners and tenants' },
  { value: 'broker', label: 'Agencies', short: 'Agencies' },
];

/**
 * Draft until somebody publishes it.
 *
 * A new package starts as a draft on purpose: it is half-priced and half-named
 * for as long as it takes to write, and publishing is the separate, deliberate
 * act that puts it in front of people.
 */
const EMPTY_PACKAGE = {
  id: '',
  name: '',
  audience: 'customer',
  credits: 100,
  bonusCredits: 0,
  priceMinor: 0,
  playProductId: '',
  status: 'draft',
};

/** What the backend falls back to when no Play product id is set. */
const defaultPlayProductId = (id) => String(id || '').trim().toLowerCase().replace(/-/g, '_');

const STATUSES = [
  { value: 'draft', label: 'Draft — nobody can buy it yet' },
  { value: 'published', label: 'Published — on sale now' },
];

/**
 * What a credit actually costs in this package.
 *
 * The one figure that makes two packages comparable — a bigger pack is only a
 * better deal if the rate falls, and that is not obvious from the price and
 * the count sitting side by side.
 */
function ratePerCredit(pack) {
  const total = (pack.credits || 0) + (pack.bonusCredits || 0);
  if (!total || !pack.priceMinor) return null;
  return Math.round(pack.priceMinor / total);
}

/**
 * The credit packages people buy.
 *
 * Two catalogues, because an agency spends at agency rates — more per ad,
 * and leads on top — so the sizes that suit them are not the sizes that suit
 * somebody letting one flat. Each is shown separately rather than as one list
 * with a column, because they are never compared against each other.
 *
 * Prices are typed and shown in rupees; the ×100 to minor units happens in
 * `lib/format.js`, so nothing here knows the scale. Taking a package off sale
 * is moving it back to draft, which keeps it on this screen. Delete removes the
 * offer itself; payments that bought it keep their own record of what was sold
 * (the package id is stamped on each one), and the full definition survives in
 * the audit log.
 */
export default function PackagesTab() {
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.CONFIG_MANAGE);

  const queryClient = useQueryClient();
  const packages = useQuery({ queryKey: ['admin-credit-packages'], queryFn: api.credits.packages });

  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [errors, setErrors] = useState({});

  const open = (pack) => {
    setEditing(pack ? { ...EMPTY_PACKAGE, ...pack } : { ...EMPTY_PACKAGE });
    setErrors({});
  };

  const close = () => {
    setEditing(null);
    setErrors({});
  };

  const save = useMutation({
    mutationFn: api.credits.upsertPackage,
    onSuccess: () => {
      toast.success(`${editing.name || 'The package'} saved.`);
      queryClient.invalidateQueries({ queryKey: ['admin-credit-packages'] });
      close();
    },
    onError: (error) => toast.error(error.message),
  });

  const isNew = editing != null && !packages.data?.some((pack) => pack.id === editing.id);

  const setField = (name, value) => setEditing((current) => ({ ...current, [name]: value }));

  const askRemove = (pack) => setRemoving(pack);

  const closeRemove = () => setRemoving(null);

  const remove = useMutation({
    mutationFn: (packageId) => api.credits.removePackage(packageId),
    onSuccess: (result) => {
      toast.success(`${result.removed.name} removed.`);
      queryClient.invalidateQueries({ queryKey: ['admin-credit-packages'] });
      closeRemove();
    },
    onError: (error) => toast.error(error.message),
  });

  const forAudience = (audience) =>
    (packages.data || []).filter((pack) => (pack.audience || 'customer') === audience);

  const submit = () => {
    /*
     * Already in minor units: the price field converts on the way in, so
     * `editing.priceMinor` is what it says it is. Converting again here is
     * what turned ₹3,999 into ₹3,99,900.
     */
    const price = editing.priceMinor == null || editing.priceMinor === '' ? null : Number(editing.priceMinor);
    const credits = Number(editing.credits);

    const found = {};
    if (!/^[a-z0-9_-]{2,60}$/i.test(String(editing.id).trim())) {
      found.id = 'Letters, numbers, hyphen and underscore only.';
    }
    if (!String(editing.name).trim()) found.name = 'Give the package a name.';
    if (!Number.isInteger(credits) || credits < 1) found.credits = 'A package has to grant at least one credit.';
    // Typed, not spun: a bonus of "ten" used to fall through `|| 0` and sell
    // the package with no bonus at all rather than saying anything.
    const bonus = String(editing.bonusCredits ?? '').trim();
    if (bonus !== '' && (!Number.isInteger(Number(bonus)) || Number(bonus) < 0)) {
      found.bonusCredits = 'Bonus credits must be a whole number, or empty.';
    }
    if (price == null || Number.isNaN(price) || price < 0) {
      found.priceMinor = 'Enter a price.';
    } else if (editing.status === 'published' && price <= 0) {
      // A draft can sit at ₹0 while it is being written; a package on sale
      // at ₹0 would hand out credits for nothing.
      found.priceMinor = 'A published package must cost more than ₹0. Save it as a draft until it is priced.';
    }
    const playProductId = String(editing.playProductId ?? '').trim().toLowerCase();
    if (playProductId && !/^[a-z0-9][a-z0-9_.]{0,138}$/.test(playProductId)) {
      found.playProductId = 'Lowercase letters, numbers, underscores and full stops only.';
    }

    setErrors(found);
    if (Object.keys(found).length) return;

    save.mutate({
      id: String(editing.id).trim().toLowerCase(),
      name: String(editing.name).trim(),
      audience: editing.audience,
      credits,
      bonusCredits: Number(editing.bonusCredits) || 0,
      priceMinor: price,
      // Empty clears it, and the pack id is used.
      playProductId,
      status: editing.status,
    });
  };

  return (
    <>
      {packages.isLoading ? (
        <Card>
          <Loading label="Loading packages" />
        </Card>
      ) : packages.isError ? (
        <Card>
          <ErrorState error={packages.error} onRetry={packages.refetch} />
        </Card>
      ) : (
        <div className="stack">
          {AUDIENCES.map((audience) => (
            <Catalogue
              key={audience.value}
              audience={audience}
              packages={forAudience(audience.value)}
              canManage={canManage}
              onNew={() => open({ ...EMPTY_PACKAGE, audience: audience.value })}
              onEdit={open}
              onRemove={askRemove}
            />
          ))}
        </div>
      )}

      <Dialog
        open={Boolean(removing)}
        onClose={closeRemove}
        title={`Delete ${removing?.name}?`}
        warning={
          removing?.purchaseCount
            ? `This package has been bought ${removing.purchaseCount} time${removing.purchaseCount === 1 ? '' : 's'}. Those payments keep their own record of what was sold and the credits stay where they are — but the offer disappears, and its full definition survives only in the audit log. Moving it back to draft takes it off sale without removing it.`
            : 'Nobody has bought this package, so removing it takes nothing away. Its definition is kept in the audit log if you need it back.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={closeRemove}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => remove.mutate(removing.id)}
              loading={remove.isPending}
            >
              Delete package
            </Button>
          </>
        }
      />

      <Dialog
        open={Boolean(editing)}
        onClose={close}
        title={isNew ? 'New package' : `Edit ${editing?.name}`}
        warning={
          editing?.status === 'published'
            ? 'Published: it is on sale at this price the moment you save. People who already bought it keep the credits they were given — this changes what the next purchase costs. Recorded in the audit log against your account.'
            : 'A draft is only visible on this screen. Nobody can buy it until you publish it.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              loading={save.isPending}
            >
              {isNew ? 'Create package' : 'Save package'}
            </Button>
          </>
        }
      >
        {editing ? (
          <div className="stack">
            <div className="field-row">
              <Field label="Name" error={errors.name} id="pack-name">
                <Input
                  id="pack-name"
                  value={editing.name}
                  error={errors.name}
                  onChange={(event) => setField('name', event.target.value)}
                  placeholder="250 credits"
                />
              </Field>
              <Field
                label="Code"
                hint={isNew ? 'Permanent. Payments reference it.' : 'Changing this creates a new package.'}
                error={errors.id}
                id="pack-id"
              >
                <Input
                  id="pack-id"
                  value={editing.id}
                  error={errors.id}
                  onChange={(event) => setField('id', event.target.value)}
                  placeholder="credits_250"
                />
              </Field>
            </div>

            <Field
              label="Sold to"
              hint={isNew ? 'Which catalogue it appears in.' : 'Moving it changes who can buy it.'}
              id="pack-audience"
            >
              <Select
                id="pack-audience"
                value={editing.audience}
                onChange={(event) => setField('audience', event.target.value)}
              >
                {AUDIENCES.map((audience) => (
                  <option key={audience.value} value={audience.value}>
                    {audience.label}
                  </option>
                ))}
              </Select>
            </Field>

            <div className="field-row">
              <Field label="Credits" error={errors.credits} id="pack-credits">
                <Input
                  id="pack-credits"
                  type="text"
                  inputMode="numeric"
                  error={errors.credits}
                  value={editing.credits}
                  onChange={(event) => setField('credits', event.target.value)}
                />
              </Field>
              <Field
                label="Bonus credits"
                hint="Shown as extra on top."
                error={errors.bonusCredits}
                id="pack-bonus"
              >
                <Input
                  id="pack-bonus"
                  type="text"
                  inputMode="numeric"
                  error={errors.bonusCredits}
                  value={editing.bonusCredits}
                  onChange={(event) => setField('bonusCredits', event.target.value)}
                />
              </Field>
              <Field label="Price (₹)" hint="What the buyer pays, all-inclusive." error={errors.priceMinor} id="pack-price">
                <Input
                  id="pack-price"
                  type="text"
                  inputMode="numeric"
                  error={errors.priceMinor}
                  value={rupeesFromMinor(editing.priceMinor)}
                  onChange={(event) => setField('priceMinor', minorFromRupees(event.target.value))}
                />
              </Field>
            </div>

            <RateHint pack={editing} />

            {/* Android sells through Google Play, at the price set on the
                Play product. A pack with no matching product is not shown
                in the Android apps at all. */}
            <Field
              label="Google Play product ID"
              hint={`Optional. Leave empty to use “${defaultPlayProductId(editing.id) || 'the code'}”. Create a one-time product with this ID in Play Console, priced the same as above.`}
              error={errors.playProductId}
              id="pack-play-product"
            >
              <Input
                id="pack-play-product"
                value={editing.playProductId ?? ''}
                error={errors.playProductId}
                onChange={(event) => setField('playProductId', event.target.value)}
                placeholder={defaultPlayProductId(editing.id) || 'credits_250'}
              />
            </Field>

            <Field
              label="Status"
              hint="A draft is only visible here. Publishing puts it on sale in the apps."
              id="pack-status"
            >
              <Select
                id="pack-status"
                value={editing.status}
                onChange={(event) => setField('status', event.target.value)}
              >
                {STATUSES.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            </Field>

          </div>
        ) : null}
      </Dialog>
    </>
  );
}

/** One catalogue: its packages, and the button that adds to it. */
function Catalogue({ audience, packages, canManage, onNew, onEdit, onRemove }) {
  return (
    <Card
      title={audience.label}
      subtitle={
        audience.value === 'broker'
          ? 'What an agency buys. They spend faster — an ad costs more, and leads come out of the same wallet.'
          : 'What somebody letting their own flat, or paying to see who to call, buys.'
      }
      actions={
        canManage ? (
          <Button variant="primary" onClick={onNew}>
            New package
          </Button>
        ) : null
      }
    >
      {!packages.length ? (
        <EmptyState
          title="Nothing on sale"
          body={`No packages for ${audience.short.toLowerCase()} yet. Until there is one, they cannot top up.`}
          action={canManage ? <Button variant="primary" onClick={onNew}>New package</Button> : null}
        />
      ) : (
        <div className="plan-grid">
          {packages.map((pack) => {
            const rate = ratePerCredit(pack);

            return (
              <article key={pack.id} className={`plan ${pack.status === 'published' ? '' : 'plan--inactive'}`}>
                <header className="plan__head">
                  <div>
                    <div className="plan__name">{pack.name}</div>
                    <div className="plan__code">{pack.id}</div>
                  </div>
                  {pack.status === 'published' ? (
                    <Badge tone="success" label="Published" />
                  ) : (
                    <Badge tone="neutral" label="Draft" />
                  )}
                </header>

                <div>
                  <div className="plan__price">
                    {money(pack.priceMinor)}
                  </div>
                  <div className="plan__secondary-price">
                    {number(pack.credits)} credits
                    {pack.bonusCredits ? ` + ${number(pack.bonusCredits)} bonus` : ''}
                    {rate ? ` · ${money(rate)} each` : ''}
                  </div>
                </div>

                {canManage ? (
                  <div className="plan__footer row">
                    <Button variant="outline" size="sm" onClick={() => onEdit(pack)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onRemove(pack)}>
                      Delete
                    </Button>
                    {pack.purchaseCount ? (
                      <span className="muted" style={{ fontSize: 12 }}>
                        {`Bought ${number(pack.purchaseCount)}×`}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/** The rate, as you type it — the number the price is actually judged on. */
function RateHint({ pack }) {
  const rate = ratePerCredit({
    credits: Number(pack.credits) || 0,
    bonusCredits: Number(pack.bonusCredits) || 0,
    priceMinor: Number(pack.priceMinor) || 0,
  });

  if (!rate) return null;

  const total = (Number(pack.credits) || 0) + (Number(pack.bonusCredits) || 0);

  return (
    <p className="muted" style={{ fontSize: 13 }}>
      {`${number(total)} credits for ${money(Number(pack.priceMinor) || 0)} — ${money(rate)} a credit.`}
    </p>
  );
}
